import React, { useState } from "react";
import { GlobalIcon } from "get-life-storybook-ts";
import Spinner from "get-life-storybook-ts/lib/components/Icons/Spinner";
import QuotePageController from "./QuotePage.controller";
import DashboardTopBar from "../../StepsCalculatorPage/Components/dashboard/components/DashboardTopBar";
import NativeShellFooter from "../../../components/NativeShell/NativeShellFooter";
import { TrustPilotSVG } from "./components/QuoteBanner";
import { DOMAIN, PHONE_NOT_COVER } from "../../../constants/Global";
import { PHONE_POST } from "../../../constants/Globals";
import { QuotePageBenefitsList } from "../../../utils/InternationlChanges";
import "./QuotePage.styles.scss";
import "./NativeQuotePage.scss";

const CAPITAL_STEP = 5000;
const BRAND = "var(--primary-color, #423EF4)";
const BRAND_SOFT = "var(--light-primary-color, #EEF0FF)";

const euros = (value: number) => `${new Intl.NumberFormat("it-IT").format(value)} €`;

/**
 * Presupuesto (calculator/4) del funnel native con la línea de diseño del Portal
 * Cliente: mismo marco (barra superior, columna centrada, pie legal sobre el
 * mismo fondo) y mismas tarjetas, sin menú lateral. Desktop: capital y
 * coberturas a la izquierda, precio + CTA fijo a la derecha; móvil: una columna
 * con el precio arriba y el CTA fijo abajo. La lógica es la de QuotePage.
 */
const NativeQuotePage = () => {
  const {
    price,
    afterPromoText,
    beforePromoText,
    btnContractLabel,
    conditionsPrice,
    handleButtonQuote,
    hasTelesubscription,
    linkRefCall,
    loadingQuote,
    navigateBack,
    setFlagLink,
    translate,
    isStickyVisible,
    guaranteeOptions,
    handleCapitalChange,
  } = QuotePageController();

  // translate() devuelve la clave si falta la traducción: fallback IT.
  const t = (key: string, fallback: string): string => {
    const value = translate(key);
    return value === key ? fallback : value;
  };

  const death = guaranteeOptions.find((g) => g.key === "DEATH");
  const min = death?.minCapital ?? 0;
  const max = death?.maxCapital ?? 0;
  const capital = death?.capital ?? 0;
  const [pending, setPending] = useState<number | null>(null);
  const sliderValue = pending ?? capital;
  const hasSlider = undefined !== death && max > min;

  const commit = () => {
    if (null !== pending && pending !== capital) {
      handleCapitalChange("DEATH", pending);
    }
  };

  const pct = hasSlider ? ((sliderValue - min) / (max - min)) * 100 : 0;
  const decimals = conditionsPrice("decimal");
  const benefits = (QuotePageBenefitsList[DOMAIN!] || QuotePageBenefitsList["default"]).map(
    (k) => translate(k)
  );

  const sectionLabel = (text: string) => <p className="nativeQuote__label">{text}</p>;

  const cta = (
    <button type="button" className="nativeQuote__cta" onClick={handleButtonQuote}>
      {btnContractLabel}
      <GlobalIcon iconName="ArrowRightIcon" size="XS" color="currentColor" />
    </button>
  );

  return (
    <div className="nativeQuote" style={{ "--shell-width": "1040px" } as React.CSSProperties}>
      <DashboardTopBar showAllianz={"it" === DOMAIN} phone={PHONE_POST} brand="always" />

      <main className="nativeQuote__page">
        <header className="nativeQuote__header">
          <h1>{translate("calculatorPage.textYourPrice")}</h1>
          <p>{translate("calculatorPage.textUpperButton")}</p>
        </header>

        <div className="nativeQuote__layout">
          <div className="nativeQuote__main">
            {hasSlider && (
              <section className="nativeQuote__card" data-testid="native-quote-capital">
                {sectionLabel(translate("upsell.guarantees.capital"))}
                <div className="nativeQuote__capitalValue">{euros(sliderValue)}</div>
                <input
                  type="range"
                  className="QuotePage__deathSlider-input"
                  min={min}
                  max={max}
                  step={CAPITAL_STEP}
                  value={sliderValue}
                  onChange={(e) => setPending(parseInt(e.target.value, 10))}
                  onPointerUp={commit}
                  onKeyUp={(e) => {
                    if (e.key === "ArrowLeft" || e.key === "ArrowRight") commit();
                  }}
                  disabled={death?.loading}
                  aria-label={translate("upsell.guarantees.capital")}
                  style={{
                    background: `linear-gradient(to right, ${BRAND} 0%, ${BRAND} ${pct}%, ${BRAND_SOFT} ${pct}%, ${BRAND_SOFT} 100%)`,
                  }}
                />
                <div className="nativeQuote__range">
                  <span>{euros(min)}</span>
                  <span>{euros(max)}</span>
                </div>
              </section>
            )}

            <section className="nativeQuote__card">
              {sectionLabel(t("nativeQuote.coverages", "Cosa include"))}
              <ul className="nativeQuote__coverages">
                <li>
                  <span>
                    <GlobalIcon iconName="FilledCheckIcon" size="XS" color="#018565" />
                    {translate("calculatorPage.textCoverageDeath")}
                  </span>
                  <strong>{euros(sliderValue)}</strong>
                </li>
                <li>
                  <span>
                    <GlobalIcon iconName="FilledCheckIcon" size="XS" color="#018565" />
                    {translate("calculatorPage.textAdditionalProtectionTerminalIllness")}
                  </span>
                  <strong>{euros(Math.round(sliderValue / 2))}</strong>
                </li>
              </ul>
            </section>

            {0 < benefits.length && (
              <section className="nativeQuote__card">
                {sectionLabel(
                  `${translate("calculatorPage.textQuestions")} · ${translate("calculatorPage.textHelp")}`
                )}
                <ul className="nativeQuote__benefits">
                  {benefits.map((b, i) => (
                    <li key={i}>
                      <span className="nativeQuote__tick" style={{ color: BRAND }}>
                        <GlobalIcon iconName="CheckIcon" size="XXS" color="currentColor" />
                      </span>
                      <span dangerouslySetInnerHTML={{ __html: b }} />
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>

          <aside className="nativeQuote__aside" id="quote_page_banner" data-testid="native-quote-price">
            {sectionLabel(t("nativeQuote.monthlyPrice", "Il tuo prezzo mensile"))}
            {beforePromoText && <p className="nativeQuote__promo">{beforePromoText}</p>}
            <div className="nativeQuote__price" aria-busy={loadingQuote}>
              {0 !== price ? (
                <span style={{ opacity: loadingQuote ? 0.3 : 1 }}>
                  <span className="nativeQuote__priceInt">{conditionsPrice()}</span>
                  {decimals && <span className="nativeQuote__priceDec">,{decimals}</span>}
                  <span className="nativeQuote__priceUnit">€/{t("nativeQuote.month", "mese")}</span>
                </span>
              ) : (
                <span className="nativeQuote__priceNa">{translate("calculator.price.notAvailable")}</span>
              )}
              {loadingQuote && (
                <span className="nativeQuote__spinner">
                  <Spinner />
                </span>
              )}
            </div>
            {afterPromoText && <p className="nativeQuote__after">{afterPromoText}</p>}
            {cta}
            <p
              className="nativeQuote__hint"
              dangerouslySetInnerHTML={{ __html: translate("calculatorPage.QuotePage.actionLabel") }}
            />
            {hasTelesubscription && (
              <button type="button" className="nativeQuote__secondary" onClick={() => setFlagLink(true)}>
                {translate("calculatorPage.QuotePage.maxCapital.button").replace("$", `${PHONE_NOT_COVER}`)}
              </button>
            )}
            <div className="nativeQuote__trust">
              <TrustPilotSVG />
              <span dangerouslySetInnerHTML={{ __html: translate("calculatorPage.QuotePage.trustpilot") }} />
            </div>
            <button type="button" className="nativeQuote__back" onClick={() => navigateBack()}>
              <GlobalIcon iconName="ArrowBackIcon" size="XS" color="currentColor" />
              {translate("back")}
            </button>
          </aside>
        </div>
      </main>

      <div className={`nativeQuote__sticky ${isStickyVisible ? "nativeQuote__sticky--on" : ""}`}>{cta}</div>

      {/* Enlace oculto que dispara la llamada (setFlagLink), como en QuotePage. */}
      {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
      <a href={`tel:${PHONE_NOT_COVER}`} ref={linkRefCall} aria-hidden="true" tabIndex={-1} />
      <NativeShellFooter />
    </div>
  );
};

export default NativeQuotePage;
